import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';
import { GeoCity, GeoCountry } from '../../../models/geo.model';
import { GeoDataProvider } from '../geo-data.provider';
import { SKIP_ERROR_TOAST } from '../../../http/http-context.tokens';

interface Dr5hnCountryRow {
  id?: number;
  name?: string;
  iso2?: string;
  phonecode?: string | number;
  phone_code?: string | number;
}

interface Dr5hnCityRow {
  id?: number;
  name?: string;
  state_code?: string;
  country_code?: string;
}

@Injectable()
export class LocalJsonGeoProvider implements GeoDataProvider {
  private readonly http = inject(HttpClient);
  private readonly countriesUrl = 'assets/geo/countries.min.json';
  private readonly citiesBaseUrl = 'assets/geo/cities';
  private readonly silentContext = new HttpContext().set(SKIP_ERROR_TOAST, true);

  private countries$?: Observable<GeoCountry[]>;
  private readonly citiesByCode = new Map<string, Observable<GeoCity[]>>();

  getCountries(): Observable<GeoCountry[]> {
    this.countries$ ??= this.http.get<Dr5hnCountryRow[]>(this.countriesUrl, { context: this.silentContext }).pipe(
      map(rows =>
        (rows || [])
          .map(row => this.mapCountry(row))
          .filter((country): country is GeoCountry => !!country)
          .sort((a, b) => a.name.localeCompare(b.name)),
      ),
      shareReplay(1),
      catchError(() => of([])),
    );
    return this.countries$;
  }

  getCitiesByCountry(countryCode: string): Observable<GeoCity[]> {
    const code = countryCode.trim().toUpperCase();
    if (!code) {
      return of([]);
    }

    let cached = this.citiesByCode.get(code);
    if (!cached) {
      cached = this.http
        .get<Dr5hnCityRow[]>(`${this.citiesBaseUrl}/${code}.json`, { context: this.silentContext })
        .pipe(
          map(rows =>
            (rows || [])
              .map(row => this.mapCity(row))
              .filter((city): city is GeoCity => !!city)
              .sort((a, b) => a.name.localeCompare(b.name)),
          ),
          shareReplay(1),
          catchError(() => of([])),
        );
      this.citiesByCode.set(code, cached);
    }
    return cached;
  }

  getPhoneCode(countryCode: string): Observable<string | null> {
    return this.resolveCountryByCode(countryCode).pipe(map(country => country?.phoneCode ?? null));
  }

  resolveCountryByCode(code: string): Observable<GeoCountry | null> {
    const normalized = code.trim().toUpperCase();
    if (!normalized) {
      return of(null);
    }
    return this.getCountries().pipe(map(countries => countries.find(country => country.code === normalized) ?? null));
  }

  resolveCountryByName(name: string): Observable<GeoCountry | null> {
    const normalized = name.trim().toLowerCase();
    if (!normalized) {
      return of(null);
    }
    return this.getCountries().pipe(
      map(
        countries =>
          countries.find(country => country.name.toLowerCase() === normalized) ??
          countries.find(country => country.name.toLowerCase().includes(normalized)) ??
          null,
      ),
    );
  }

  private mapCountry(row: Dr5hnCountryRow): GeoCountry | null {
    const code = String(row.iso2 ?? '')
      .trim()
      .toUpperCase();
    const name = String(row.name ?? '').trim();
    if (!code || !name) {
      return null;
    }
    const rawPhone = row.phonecode ?? row.phone_code ?? '';
    const phoneCode = String(rawPhone).replace(/\D+/g, '');
    return { code, name, phoneCode };
  }

  private mapCity(row: Dr5hnCityRow): GeoCity | null {
    const id = Number(row.id);
    const name = String(row.name ?? '').trim();
    if (!Number.isFinite(id) || !name) {
      return null;
    }
    const stateCode = row.state_code ? String(row.state_code).trim() : undefined;
    return { id, name, stateCode: stateCode || undefined };
  }
}
